import {
  APIAttachment,
  APIDocumentArchiveFolder,
  ArdoqId,
  isContentTypeSupportedByImagePreview,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  toWorkspaceDocumentArchivePageViewProps,
  WorkspaceDocumentArchiveCommands,
} from '../../workspaceManager/pages/documentArchive/toWorkspaceDocumentArchivePageViewProps';
import type { DragState } from '../../workspaceManager/streams/dragState$';
import { DocumentArchiveState } from '../DocumentArchivePage';
import {
  ContextMenuItem,
  GroupedEntity,
  TablePrimaryActionButtonProps,
} from '../DocumentArchivePageView';
import {
  DocumentArchiveCommands,
  getGlobalContextMenuItems,
  toDocumentArchivePageViewProps,
} from '../toDocumentArchivePageViewProps';
import {
  commitSelection,
  setDocumentArchiveFilePickerActiveTab,
} from './actions';
import {
  DocumentArchiveFilePickerPageViewProps,
  DocumentArchiveGlobalFilePickerPageViewProps,
  DocumentArchiveWorkspaceFilePickerPageViewProps,
  TabsProps,
} from './DocumentArchiveFilePickerPageView';
import {
  FilePickerState,
  ImpureFilePickerState,
  CommittedAttachments,
  TabId,
  WorkspaceIncludedFilePickerState,
} from './types';

export const toDocumentArchiveFilePickerPageViewProps = (
  state: DocumentArchiveState,
  globalFolders: APIDocumentArchiveFolder[],
  globalAttachments: APIAttachment[],
  typeId: ArdoqId,
  locale: Locale,
  globalCommands: DocumentArchiveCommands,
  filePickerState: FilePickerState,
  workspaceAttachments: APIAttachment[],
  workspaceFolders: APIDocumentArchiveFolder[],
  workspaceCommands: WorkspaceDocumentArchiveCommands,
  dragState: DragState,
  impureState: ImpureFilePickerState | null,
  i18nLocale: I18nLocale
): DocumentArchiveFilePickerPageViewProps & CommittedAttachments => {
  const prefilteredGlobalAttachments =
    impureState?.filter === 'image'
      ? globalAttachments.filter(isContentTypeSupportedByImagePreview)
      : globalAttachments;

  const prefilteredWorkspaceAttachments =
    impureState?.filter === 'image'
      ? workspaceAttachments.filter(isContentTypeSupportedByImagePreview)
      : workspaceAttachments;

  const props =
    filePickerState.activeTab === 'global' ||
    !impureState ||
    !impureState.workspaceId
      ? toDocumentArchiveGlobalFilePickerPageViewProps(
          state,
          globalFolders,
          prefilteredGlobalAttachments,
          typeId,
          locale,
          globalCommands,
          filePickerState,
          impureState,
          i18nLocale
        )
      : toDocumentArchiveWorkspaceFilePickerPageViewProps(
          state,
          globalAttachments,
          locale,
          globalCommands,
          filePickerState,
          prefilteredWorkspaceAttachments,
          workspaceFolders,
          workspaceCommands,
          dragState,
          impureState,
          i18nLocale
        );

  const committedSelectionIds = new Set(filePickerState.committedSelection);
  const committedAttachments = [
    ...globalAttachments,
    ...workspaceAttachments,
  ].filter(attachment => committedSelectionIds.has(attachment._id));

  return {
    ...props,
    committedAttachments:
      committedAttachments.length > 0 ? committedAttachments : null,
  };
};

const toSelectedAttachments = (
  rowData: GroupedEntity,
  allSelectedRows: GroupedEntity[]
): GroupedEntity[] => {
  const isCurrentRowSelected = allSelectedRows.some(
    row => row.id === rowData.id
  );

  if (isCurrentRowSelected) {
    return allSelectedRows.filter(row => !row._group.isFolder);
  }

  if (rowData._group.isFolder) {
    return [];
  }

  return [rowData];
};

const toFilePickerContextMenuItems = (
  rowData: GroupedEntity,
  selectedRows: GroupedEntity[],
  i18nLocale: I18nLocale
): ContextMenuItem[] => {
  const selectedAttachments = toSelectedAttachments(rowData, selectedRows);

  if (selectedAttachments.length === 0) {
    return [];
  }

  const label =
    selectedAttachments.length === 1
      ? t(
          i18nLocale,
          'documentArchive:filePicker.useSelectedAttachment',
          'Use selected attachment'
        )
      : t(
          i18nLocale,
          'documentArchive:filePicker.useSelectedAttachmentsWithCount',
          { count: selectedAttachments.length },
          'Use selected attachments ({{count}})'
        );

  return [
    {
      name: label,
      label,
      onClick: () =>
        dispatchAction(commitSelection(selectedAttachments.map(att => att.id))),
    },
  ];
};

export const toDocumentArchiveGlobalFilePickerPageViewProps = (
  state: DocumentArchiveState,
  globalFolders: APIDocumentArchiveFolder[],
  globalAttachments: APIAttachment[],
  typeId: ArdoqId,
  locale: Locale,
  globalCommands: DocumentArchiveCommands,
  filePickerState: FilePickerState,
  impureState: ImpureFilePickerState | null,
  i18nLocale: I18nLocale
): DocumentArchiveGlobalFilePickerPageViewProps => {
  const props = toDocumentArchivePageViewProps(
    state,
    globalFolders,
    globalAttachments,
    typeId,
    locale,
    globalCommands,
    i18nLocale
  );

  const rowsWithContextMenu = props.table.rows.map(row => ({
    ...row,
    getContextMenuItems: toGetContextMenuItems(
      globalAttachments,
      globalCommands,
      i18nLocale
    ),
  }));

  const filteredSelection = globalAttachments.filter(att =>
    state.selectedIds.includes(att._id)
  );

  return {
    type: 'global',
    tabs:
      impureState?.workspaceId === undefined
        ? null
        : toTabsProps(filePickerState, i18nLocale),
    ...props,
    table: {
      ...props.table,
      tableHeight: '400px',
      floatingActionBar: null,
      tablePrimaryActionButton: toTablePrimaryActionButtonProps(
        filteredSelection,
        impureState
      ),
      rows: rowsWithContextMenu,
    },
    title: t(
      i18nLocale,
      'documentArchive:filePicker.globalTitle',
      'Global attachment file picker'
    ),
  };
};

const toTabsProps = (
  filePickerState: FilePickerState,
  i18nLocale: I18nLocale
): TabsProps => ({
  items: [
    {
      id: 'global',
      label: t(i18nLocale, 'documentArchive:filePicker.tabs.global', 'Global'),
    },
    {
      id: 'workspace',
      label: t(
        i18nLocale,
        'documentArchive:filePicker.tabs.workspace',
        'Workspace'
      ),
    },
  ],
  selectedId: filePickerState.activeTab,
  onChange: (tabId: TabId) =>
    dispatchAction(setDocumentArchiveFilePickerActiveTab(tabId)),
  moreButtonLabel: t(i18nLocale, 'common:labels.more', 'More'),
});

const toDocumentArchiveWorkspaceFilePickerPageViewProps = (
  state: DocumentArchiveState,
  globalAttachments: APIAttachment[],
  locale: Locale,
  globalCommands: DocumentArchiveCommands,
  filePickerState: FilePickerState,
  workspaceAttachments: APIAttachment[],
  workspaceFolders: APIDocumentArchiveFolder[],
  workspaceCommands: WorkspaceDocumentArchiveCommands,
  dragState: DragState,
  impureState: WorkspaceIncludedFilePickerState,
  i18nLocale: I18nLocale
): DocumentArchiveWorkspaceFilePickerPageViewProps => {
  const props = toWorkspaceDocumentArchivePageViewProps(
    state,
    workspaceFolders,
    workspaceAttachments,
    globalAttachments,
    impureState.workspaceId,
    locale,
    workspaceCommands,
    dragState,
    i18nLocale
  );

  const rowsWithContextMenu = props.table.rows.map(row => ({
    ...row,
    getContextMenuItems: toGetContextMenuItems(
      workspaceAttachments,
      globalCommands,
      i18nLocale
    ),
  }));

  const filteredSelection = workspaceAttachments.filter(att =>
    state.selectedIds.includes(att._id)
  );

  return {
    type: 'workspace',
    tabs: toTabsProps(filePickerState, i18nLocale),
    ...props,
    table: {
      ...props.table,
      tableHeight: '400px',
      floatingActionBar: null,
      tablePrimaryActionButton: toTablePrimaryActionButtonProps(
        filteredSelection,
        impureState
      ),
      rows: rowsWithContextMenu,
    },
    title: t(
      i18nLocale,
      'documentArchive:filePicker.workspaceTitle',
      'Workspace attachment file picker'
    ),
  };
};

const toTablePrimaryActionButtonProps = (
  filteredSelection: APIAttachment[],
  impureState: ImpureFilePickerState | null
): TablePrimaryActionButtonProps => ({
  label: impureState
    ? `${impureState.acceptButtonLabel} (${filteredSelection.length})`
    : '',
  disabled: filteredSelection.length === 0,
  onClick: () =>
    dispatchAction(commitSelection(filteredSelection.map(att => att._id))),
  popover: null,
});

const toGetContextMenuItems =
  (
    attachments: APIAttachment[],
    commands: DocumentArchiveCommands,
    i18nLocale: I18nLocale
  ) =>
  (
    rowData: GroupedEntity,
    selectedRows: GroupedEntity[]
  ): ContextMenuItem[] => {
    const contextMenuItems = getGlobalContextMenuItems(
      attachments,
      commands.documentArchiveTable,
      i18nLocale
    )(rowData, selectedRows);

    const filePickerContextMenuItems = toFilePickerContextMenuItems(
      rowData,
      selectedRows,
      i18nLocale
    );

    const deleteContextMenuItem = contextMenuItems.find(
      item => item.color === 'textError'
    );

    const globalContextMenuItemsWithoutDelete = contextMenuItems.filter(
      item => item.color !== 'textError'
    );

    return [
      ...filePickerContextMenuItems,
      ...globalContextMenuItemsWithoutDelete,
      ...[deleteContextMenuItem].filter(isTruthy),
    ];
  };
